import { Descriptions } from "@/components/v2";
import { Card } from "@rumbledirect/ui";
import { type DescriptionsProps } from "antd";
import type { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchEmailSentDetailsById } from "@/lib/api/resources/emails";
import { fetchSmsSentDetailsById } from "../../../../lib/api/resources/sms";
import { CorrespondenceItem } from "../../dossiers/components/correspondence-item";
import ModalEmailCorrespondence from "../../dossiers/components/modal-email-correspondence";
import ModalSmsCorrespondence from "../../dossiers/components/modal-sms-correspondence";
import athlonContent from "../lib/athlon.html?raw";
import ayvensContent from "../lib/ayvens.html?raw";
import { DossierUsageEmailSent } from "@rumble-it/common-lib/types/models/EmailSent.interface";

interface CorrespondenceCardProps {
  dossier: Dossier | undefined;
  isLoading?: boolean;
}

export const AgentDossierCorrespondenceCard = ({
  dossier,
  isLoading,
}: CorrespondenceCardProps) => {
  const [isEmailModalOpen, setEmailModalOpen] = useState(false);
  const [isSmsModalOpen, setSmsModalOpen] = useState(false);
  const [selectedCorrespondence, setSelectedCorrespondence] = useState<{
    id: string;
    type: "email" | "sms";
  } | null>(null);

  const createdAt =
    dossier?.leaseCompany?.name === "Athlon"
      ? "7-11-2025 13:32"
      : "4-11-2025 16:46";
  const subject =
    dossier?.leaseCompany?.name === "Athlon"
      ? "Reparatieverzoek - in afwachting van bevestiging aan klant | Kenteken GGN-83-S | 26-11-2025 15:00"
      : "Schademelding 25786144 via My Ayvens";

  const htmlContent =
    dossier?.leaseCompany?.name === "Athlon" ? athlonContent : ayvensContent;

  const emailSentQuery = useQuery({
    queryKey: ["emailSentDetails", selectedCorrespondence?.id],
    queryFn: async () => {
      if (
        !selectedCorrespondence?.id ||
        selectedCorrespondence?.type !== "email"
      )
        return null;

      const from =
        dossier?.leaseCompany?.name === "Athlon"
          ? "CustomerCloud <info@customercloud.nl >"
          : "noreply@leaseplan.com <noreply@leaseplan.com>";
      const to = "Schadenet Van As Dordrecht <van.as.dordrecht@schadenet.nl>";

      if (selectedCorrespondence.id === "fake-company-email") {
        return {
          id: "fake-company-email",
          from: [from],
          to: [to],
          cc: [],
          bcc: [],
          replyTo: [],
          subject: subject,
          html: htmlContent,
          messageId: "f8q6kaWamRFTnHVhtxkSij",
          attachments: [],
          emailTemplateId: null,
          plain: "",
          createdAt: "nu",
          "@type": "EmailSent",
          "@id": "fake-company-email",
          subjectWithoutStatementsAndExpressions: "E-mail van opdrachtgever",
        };
      }
      return fetchEmailSentDetailsById(selectedCorrespondence?.id);
    },
  });

  const smsSentQuery = useQuery({
    queryKey: ["smsSentDetails", selectedCorrespondence?.id],
    queryFn: async () => {
      if (!selectedCorrespondence?.id || selectedCorrespondence?.type !== "sms")
        return null;
      return fetchSmsSentDetailsById(selectedCorrespondence?.id);
    },
  });

  const openCorrespondenceModal = (id: string, type: "email" | "sms") => {
    setSelectedCorrespondence({ id, type });
    if (type === "email") {
      setEmailModalOpen(true);
    } else if (type === "sms") {
      setSmsModalOpen(true);
    }
  };

  const companyEmailCorrespondeItems: DossierUsageEmailSent<string>[] = [
    {
      "@type": "DossierUsageEmailSent",
      "@id": "fake-company-email",
      emailSent: {
        "@type": "EmailSent",
        "@id": "fake-company-email",
        id: "fake-company-email",
        createdAt: createdAt,
        subjectWithoutStatementsAndExpressions: subject,
      },
    },
  ];

  const correspondenceItems = [
    {
      label: "E-mail van opdrachtgever",
      children: (
        <CorrespondenceItem
          dossierUsageItems={companyEmailCorrespondeItems}
          dossierUsageType="email"
          emptyStateText="Geen e-mails verzonden"
          openCorrespondenceModal={openCorrespondenceModal}
        />
      ),
    },
    {
      label: "E-mail naar hersteller",
      children: (
        <CorrespondenceItem
          dossierUsageItems={dossier?.dossierUsageEmailSentServicePartner}
          dossierUsageType="email"
          emptyStateText="Geen e-mails verzonden"
          openCorrespondenceModal={openCorrespondenceModal}
        />
      ),
    },
    ...(dossier?.dealershipCompany
      ? [
          {
            label: "E-mail naar dealerbedrijf",
            children: (
              <CorrespondenceItem
                dossierUsageItems={dossier?.dossierUsageEmailSentDealership}
                dossierUsageType="email"
                emptyStateText="Geen e-mails verzonden"
                openCorrespondenceModal={openCorrespondenceModal}
              />
            ),
          },
        ]
      : []),
    {
      label: "E-mail naar bestuurder",
      children: (
        <CorrespondenceItem
          dossierUsageItems={dossier?.dossierUsageEmailSentDriver}
          dossierUsageType="email"
          emptyStateText="Geen e-mails verzonden"
          openCorrespondenceModal={openCorrespondenceModal}
        />
      ),
    },
    {
      label: "SMS naar klant",
      children: (
        <CorrespondenceItem
          dossierUsageItems={dossier?.dossierUsageSmsSentDriver}
          dossierUsageType="sms"
          emptyStateText="Geen SMS verzonden"
          openCorrespondenceModal={openCorrespondenceModal}
        />
      ),
    },
    {
      label: "Backoffice SHB (DW Pro)",
      children: (
        <CorrespondenceItem
          dossierUsageItems={dossier?.dossierUsageDwProSent}
          dossierUsageType="DwPro"
          emptyStateText="Niet verzonden naar DW Pro"
        />
      ),
    },
    {
      label: "ImageCapture naar berijder",
      children: (
        <CorrespondenceItem
          dossierUsageItems={dossier?.imageCaptureSents}
          dossierUsageType="ImageCapture"
          emptyStateText="Geen ImageCapture verzonden"
        />
      ),
    },
  ];

  const items: DescriptionsProps["items"] = correspondenceItems.map(
    (item, index) => ({
      key: index,
      label: item.label,
      children: item.children ?? null,
    }),
  );

  return (
    <Card loading={isLoading} className="w-full" title="Correspondentie">
      <Descriptions items={items} />
      <ModalEmailCorrespondence
        title="Inhoud van e-mail"
        emailDetails={emailSentQuery.data}
        isOpen={isEmailModalOpen}
        setIsOpen={setEmailModalOpen}
        loading={emailSentQuery.isLoading}
      />
      <ModalSmsCorrespondence
        title="Inhoud van sms"
        smsDetails={smsSentQuery.data}
        isOpen={isSmsModalOpen}
        setIsOpen={setSmsModalOpen}
        loading={smsSentQuery.isLoading}
      />
    </Card>
  );
};
